/**
 * VÉRAULT Portal — Advisor Overview Command Center (Wave 26.19c)
 * ================================================================
 * The living operational command center for the entire advisor portal.
 * Eight anchored zones served by a single read aggregate.
 *
 *   #today      — Immediate attention
 *   #journeys   — Active journey cards
 *   #concierge  — Recent / unread concierge threads
 *   #departures — Trips leaving in next 30 days
 *   #alerts     — Subtle operational alerts
 *   #financial  — Outstanding / paid pulse + 3 tiles
 *   #workspace  — Quick-action shortcuts
 *   #timeline   — Recent activity feed
 *
 * GET /api/advisor/overview returns every fact the UI needs.
 */
import React from "react";
import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import {
  CircleAlert,
  MessageSquare,
  Plane,
  ScrollText,
  FileText,
  CreditCard,
  CheckCircle2,
  ChevronRight,
  Plus,
  UserPlus,
  Send,
  Sparkles,
  ArrowUpRight,
  Eye,
  Lock,
  Clock,
} from "lucide-react";
import {
  Surface,
  Heading,
  PortalButton,
  StatusTag,
  Empty,
  Skeleton,
  api,
  fmt,
} from "../../index";
import NewJourneyModal from "../../components/NewJourneyModal";
import { useAuth } from "../../../contexts/AuthContext";
import { WhatChangedCard } from "./DeskSurfaces";
import InboundRepliesChip from "./InboundRepliesChip";

const sectionClass = "pt-10 mt-10 border-t border-[rgba(0,0,0,0.05)] scroll-mt-32";

// ───────────────────────────────────────────────────────────────────
// Wave 30.3 · Phase 9e — Ecosystem health strip
// At-a-glance signal of the entire advisor↔client ecosystem health.
// Polls /admin/ecosystem-health every 60s + refreshes on advisor live-sync
// events. Surfaces conflicts in last 24h, events volume, and the
// client/advisor pending-action rollups.
// ───────────────────────────────────────────────────────────────────
function EcosystemHealthStrip() {
  const [health, setHealth] = React.useState(null);
  React.useEffect(() => {
    let mounted = true;
    const fetch = () => {
      api.get("/admin/ecosystem-health")
        .then(({ data }) => { if (mounted) setHealth(data); })
        .catch(() => { if (mounted) setHealth(null); });
    };
    fetch();
    const t = setInterval(fetch, 60000);
    const handler = () => fetch();
    window.addEventListener("vt:advisor-sync:any", handler);
    return () => {
      mounted = false;
      clearInterval(t);
      window.removeEventListener("vt:advisor-sync:any", handler);
    };
  }, []);
  if (!health) return null;
  const conflicts = health.conflicts_last_24h || 0;
  const healthy = health.healthy && conflicts === 0;
  return (
    <div
      className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 px-4 py-2.5 rounded-md border border-[rgba(0,0,0,0.06)] bg-white"
      data-testid="ecosystem-health-strip"
    >
      <div className="flex items-center gap-3 min-w-0 flex-wrap">
        <span
          className={`inline-block size-2 rounded-full shrink-0 ${
            healthy ? "bg-[hsl(var(--accent-gold))]" : "bg-[#C58A4E]"
          }`}
          aria-label={healthy ? "Healthy ecosystem" : "Recent conflicts"}
          data-testid="ecosystem-health-dot"
        />
        <span className="font-mono text-[10px] uppercase tracking-[0.20em] text-[hsl(var(--foreground-muted))] shrink-0">
          Ecosystem
        </span>
        <span className="font-serif text-[13.5px] text-[hsl(var(--foreground))] truncate">
          {healthy
            ? "All quiet · in sync"
            : `${conflicts} concurrency notice${conflicts > 1 ? "s" : ""} · last 24h`}
        </span>
      </div>
      <div className="flex items-center gap-5 flex-wrap shrink-0">
        <HealthStat label="Events · 24h" value={health.ecosystem_events_last_24h ?? 0} testid="health-events" />
        <HealthStat label="Client owes" value={health.pending_client_actions ?? 0} testid="health-client-pending" />
        <HealthStat label="You owe" value={health.pending_advisor_actions ?? 0} testid="health-advisor-pending" />
      </div>
    </div>
  );
}

function HealthStat({ label, value, testid }) {
  return (
    <div className="flex items-baseline gap-2" data-testid={testid}>
      <span className="font-serif text-[15px] text-[hsl(var(--foreground))] tabular-nums">{value}</span>
      <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))]">{label}</span>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────
// Wave 30.5 · Phase 10d — Upcoming Triggers Strip
// The advisor's at-a-glance window into the orchestrator's next moves.
// Pure operational read; never chatty. Polls /admin/orchestrator/upcoming
// every 60s. Mono labels, hairline divider — feels like a flight board.
// ───────────────────────────────────────────────────────────────────
const TRIGGER_LABELS = {
  deposit_reminder: "Deposit reminder",
  prep_pulse: "Prep pulse",
  final_check: "Final check",
  reveal_imminent: "Reveal imminent",
  balance_reminder: "Balance reminder",
};

function _formatFireAt(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const now = new Date();
  const diffMs = d - now;
  const absH = Math.abs(diffMs) / 3600000;
  if (absH < 1) {
    const m = Math.round(diffMs / 60000);
    if (m <= 0) return "now";
    return `in ${m}m`;
  }
  if (absH < 24) {
    return `in ${Math.round(absH)}h`;
  }
  const days = Math.round(absH / 24);
  return `in ${days}d`;
}

function UpcomingTriggersStrip() {
  const [items, setItems] = React.useState(null);
  React.useEffect(() => {
    let mounted = true;
    const fetch = () => {
      api.get("/admin/orchestrator/upcoming?limit=5")
        .then(({ data }) => { if (mounted) setItems(Array.isArray(data) ? data : []); })
        .catch(() => { if (mounted) setItems([]); });
    };
    fetch();
    const t = setInterval(fetch, 60000);
    const handler = () => fetch();
    window.addEventListener("vt:advisor-sync:any", handler);
    return () => {
      mounted = false;
      clearInterval(t);
      window.removeEventListener("vt:advisor-sync:any", handler);
    };
  }, []);
  if (items === null) return null;
  return (
    <div
      className="mt-3 flex items-center gap-4 px-4 py-2.5 rounded-md border border-[rgba(0,0,0,0.06)] bg-white overflow-x-auto"
      data-testid="upcoming-triggers-strip"
    >
      <span className="font-mono text-[10px] uppercase tracking-[0.20em] text-[hsl(var(--foreground-muted))] shrink-0">
        Upcoming
      </span>
      {items.length === 0 ? (
        <span
          className="font-accent italic text-[13px] text-[hsl(var(--foreground-muted))]"
          data-testid="upcoming-triggers-empty"
        >
          No scheduled actions.
        </span>
      ) : (
        <ul className="flex items-center gap-4 flex-wrap" data-testid="upcoming-triggers-list">
          {items.map((it) => (
            <li
              key={it.id}
              className="flex items-baseline gap-1.5"
              data-testid={`upcoming-trigger-${it.kind}`}
            >
              <span className="font-serif text-[13.5px] text-[hsl(var(--foreground))]">
                {TRIGGER_LABELS[it.kind] || it.kind}
              </span>
              <span className="font-mono text-[9.5px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))]">
                {it.client_name || it.trip_label || "—"} · {_formatFireAt(it.fire_at)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────
// Data hook
// ───────────────────────────────────────────────────────────────────
function useOverview() {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const refresh = React.useCallback(async () => {
    try {
      const { data: payload } = await api.get("/advisor/overview");
      setData(payload);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);
  React.useEffect(() => {
    refresh();
    const iv = setInterval(refresh, 60_000); // gentle 60s refresh
    return () => clearInterval(iv);
  }, [refresh]);
  return { data, loading, error, refresh };
}

// ───────────────────────────────────────────────────────────────────
// Editorial header — salutation + pulse
// ───────────────────────────────────────────────────────────────────
function OverviewHeader({ user, data, onNewJourney }) {
  const GENERIC = React.useMemo(() => new Set(["owner", "admin", "advisor", "user", "developer", "dev"]), []);
  const rawName = (user?.name || user?.email || "").split(/[@\s]/)[0];
  const name = rawName && !GENERIC.has(rawName.toLowerCase()) ? rawName : "";
  const hour = new Date().getHours();
  const salutation = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
  return (
    <div className="flex items-start justify-between gap-6 flex-wrap" data-testid="overview-header">
      <div>
        <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--accent-gold))]">VÉRAULT · OVERVIEW</p>
        <h1 className="mt-3 font-serif text-[28px] sm:text-[32px] text-[hsl(var(--foreground))]">
          {salutation}{name ? `, ${name.charAt(0).toUpperCase() + name.slice(1)}` : ""}.
        </h1>
        {data?.pulse && (
          <p className="mt-3 font-accent italic text-[14px] text-[hsl(var(--foreground-secondary))]" data-testid="overview-pulse">
            {data.pulse}
          </p>
        )}
      </div>
      <PortalButton
        variant="primary"
        size="md"
        icon={<Plus className="size-3.5" />}
        onClick={onNewJourney}
        data-testid="overview-new-journey"
      >
        New experience
      </PortalButton>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────
// #today — Immediate attention
// ───────────────────────────────────────────────────────────────────
const IMMEDIATE_ICONS = {
  departure: Plane,
  message: MessageSquare,
  agreement: ScrollText,
  default: CircleAlert,
};

function TodaySection({ data }) {
  const items = data?.immediate || [];
  return (
    <section id="today" className="scroll-mt-32" data-testid="overview-section-today">
      <Heading level="eyebrow">Today &amp; immediate attention</Heading>
      {items.length === 0 ? (
        <p className="mt-3 font-accent italic text-[13.5px] text-[hsl(var(--foreground-muted))]" data-testid="today-quiet">
          A calm morning. Nothing urgent — what a beautiful place to begin.
        </p>
      ) : (
        <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-2.5" data-testid="today-list">
          {items.map((i) => {
            const Icon = IMMEDIATE_ICONS[i.kind] || IMMEDIATE_ICONS.default;
            return (
              <li key={i.id} data-testid={`today-${i.id}`}>
                <Link to={i.deeplink} className="block p-3.5 rounded-md border border-[rgba(0,0,0,0.06)] bg-[hsl(var(--background-paper))] hover:bg-white hover:border-[hsl(var(--accent-gold))]/30 transition-colors group">
                  <div className="flex items-center gap-3">
                    <Icon className={`size-4 flex-shrink-0 ${i.tone === "gold" ? "text-[hsl(var(--accent-gold))]" : "text-[hsl(var(--foreground-muted))]"}`} strokeWidth={1.5} />
                    <div className="flex-1 min-w-0">
                      <p className="font-body text-[14px] truncate">{i.title}</p>
                      <p className="font-accent italic text-[12.5px] text-[hsl(var(--foreground-muted))] truncate">{i.detail}</p>
                    </div>
                    <ChevronRight className="size-3.5 text-[hsl(var(--foreground-muted))] group-hover:text-[hsl(var(--foreground))]" strokeWidth={1.5} />
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

// ───────────────────────────────────────────────────────────────────
// #journeys — Active journey cards
// ───────────────────────────────────────────────────────────────────
function JourneysSection({ data }) {
  const journeys = (data?.journeys || []).slice(0, 12);
  return (
    <section id="journeys" className={sectionClass} data-testid="overview-section-journeys">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <Heading level="eyebrow">Active experiences</Heading>
          <p className="mt-2 font-accent italic text-[13px] text-[hsl(var(--foreground-muted))]">
            {data?.summary?.active_journeys || 0} in motion
          </p>
        </div>
        <Link to="/portal/advisor/journeys" className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))] inline-flex items-center gap-1" data-testid="overview-see-all-journeys">
          See all <ArrowUpRight className="size-3" strokeWidth={1.5} />
        </Link>
      </div>
      {journeys.length === 0 ? (
        <Empty className="mt-5" title="No active experiences yet" icon={<Plane className="size-6" strokeWidth={1.25} />} />
      ) : (
        <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="overview-journey-list">
          {journeys.map((j, idx) => <JourneyCard key={`${j.id || "j"}-${idx}`} j={j} />)}
        </ul>
      )}
    </section>
  );
}

function JourneyCard({ j }) {
  const name = j.internal_trip_name || j.client_trip_title || "Experience";
  return (
    <li data-testid={`overview-journey-${j.id}`}>
      <Link to={`/portal/advisor/journeys/${j.id}`} className="block p-4 rounded-md border border-[rgba(0,0,0,0.06)] bg-[hsl(var(--background-paper))] hover:bg-white hover:border-[hsl(var(--accent-gold))]/30 transition-colors h-full">
        <div className="flex items-start justify-between gap-2 mb-2">
          <p className="font-serif text-[15px] leading-tight truncate flex-1">{name}</p>
          {j.any_masked && (
            <Lock className="size-3 text-[hsl(var(--accent-gold))] flex-shrink-0 mt-1" strokeWidth={1.7} title="Reveal mode" />
          )}
        </div>
        <p className="font-accent italic text-[12.5px] text-[hsl(var(--foreground-muted))] truncate">
          {j.client_name || "—"}{j.destinations ? ` · ${j.destinations}` : ""}
        </p>
        <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))]">
          {j.days_to_departure != null ? (
            j.days_to_departure < 0
              ? `Returned ${-j.days_to_departure}d ago`
              : j.days_to_departure === 0
                ? `Departs today · ${fmt.formatDate(j.trip_start_date)}`
                : `${j.days_to_departure}d · ${fmt.formatDate(j.trip_start_date)}`
          ) : "No dates yet"}
          {" · "}
          {(j.lifecycle_stage || "planning").replace(/_/g, " ")}
        </p>
        {(j.unread_messages > 0 || j.blocker_count > 0) && (
          <div className="mt-2.5 flex items-center gap-1.5 flex-wrap">
            {j.unread_messages > 0 && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-mono text-[9px] uppercase tracking-[0.16em] bg-[hsl(var(--accent-gold))]/12 text-[hsl(var(--accent-gold))]" data-testid={`journey-unread-${j.id}`}>
                <MessageSquare className="size-2.5" strokeWidth={1.7} /> {j.unread_messages}
              </span>
            )}
            {j.outstanding_count > 0 && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-mono text-[9px] uppercase tracking-[0.16em] bg-[hsl(var(--foreground))]/5 text-[hsl(var(--foreground-muted))]">
                <CreditCard className="size-2.5" strokeWidth={1.7} /> {j.outstanding_count}
              </span>
            )}
            {j.pending_agreements > 0 && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-mono text-[9px] uppercase tracking-[0.16em] bg-[hsl(var(--foreground))]/5 text-[hsl(var(--foreground-muted))]">
                <ScrollText className="size-2.5" strokeWidth={1.7} /> {j.pending_agreements}
              </span>
            )}
          </div>
        )}
      </Link>
    </li>
  );
}

// ───────────────────────────────────────────────────────────────────
// #concierge — Recent + unread conversations
// ───────────────────────────────────────────────────────────────────
function ConciergeSection({ data }) {
  const rows = (data?.concierge?.rows || []).slice(0, 6);
  const unread = data?.concierge?.unread_total || 0;
  return (
    <section id="concierge" className={sectionClass} data-testid="overview-section-concierge">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <Heading level="eyebrow">Concierge desk</Heading>
          <p className="mt-2 font-accent italic text-[13px] text-[hsl(var(--foreground-muted))]">
            {unread > 0 ? `${unread} awaiting your reply.` : "All threads are read — well kept."}
          </p>
        </div>
        <Link to="/portal/advisor/concierge" className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))] inline-flex items-center gap-1">
          Open concierge <ArrowUpRight className="size-3" strokeWidth={1.5} />
        </Link>
      </div>
      {rows.length === 0 ? (
        <Empty className="mt-5" title="No active conversations" icon={<MessageSquare className="size-6" strokeWidth={1.25} />} />
      ) : (
        <ul className="mt-5 divide-y divide-[rgba(0,0,0,0.05)]" data-testid="overview-concierge-list">
          {rows.map((c) => {
            const isClientWaiting = (c.last_sender_role || "") === "client" && (c.unread_for_advisor || 0) > 0;
            return (
              <li key={c.id} data-testid={`overview-concierge-${c.id}`}>
                <Link to={`/portal/advisor/journeys/${c.trip_id}?tab=client-lounge#messages`} className="py-3 flex items-center gap-3 hover:bg-[hsl(var(--background-paper))]/60 rounded-md px-2 -mx-2 transition-colors">
                  <MessageSquare className={`size-4 flex-shrink-0 ${isClientWaiting ? "text-[hsl(var(--accent-gold))]" : "text-[hsl(var(--foreground-muted))]"}`} strokeWidth={1.5} />
                  <div className="flex-1 min-w-0">
                    <p className="font-body text-[14px] truncate">
                      {c.client_name || c.trip_name || "Conversation"}
                    </p>
                    <p className="font-accent italic text-[12.5px] text-[hsl(var(--foreground-muted))] truncate">
                      {c.last_message_preview || "—"}
                    </p>
                  </div>
                  {(c.unread_for_advisor || 0) > 0 && (
                    <StatusTag tone="gold">{c.unread_for_advisor}</StatusTag>
                  )}
                  <span className="font-mono text-[9.5px] uppercase tracking-[0.16em] text-[hsl(var(--foreground-muted))] tabular-nums w-16 text-right">
                    {c.last_message_at ? fmt.formatDate(c.last_message_at) : ""}
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

// ───────────────────────────────────────────────────────────────────
// Weather glance (small, italic, F-default) — single shared fetcher
// ───────────────────────────────────────────────────────────────────
const _weatherCache = new Map();

function useWeatherGlance(destination) {
  const [data, setData] = React.useState(() => _weatherCache.get(destination) || null);
  React.useEffect(() => {
    if (!destination) return;
    if (_weatherCache.has(destination)) {
      setData(_weatherCache.get(destination));
      return;
    }
    let alive = true;
    api.get(`/weather/${encodeURIComponent(destination)}`)
      .then(({ data: payload }) => {
        if (!alive) return;
        _weatherCache.set(destination, payload);
        setData(payload);
      })
      .catch(() => { _weatherCache.set(destination, null); });
    return () => { alive = false; };
  }, [destination]);
  return data;
}

function WeatherGlance({ destination }) {
  // Read the user's preferred unit (defaults to fahrenheit per Wave 26.19d)
  const unit = (typeof window !== "undefined" && localStorage.getItem("verault_temp_unit")) || "fahrenheit";
  const data = useWeatherGlance(destination);
  if (!data) return null;
  const current = data.current || {};
  const tempStr = unit === "celsius" ? current.temperature : current.temperature_f;
  if (!tempStr) return null;
  const condition = current.condition || "";
  // Use only the FIRST destination word/segment if comma/middot separated
  const firstDest = (destination || "").split(/[·,]/)[0].trim();
  return (
    <span className="font-accent italic text-[11.5px] text-[hsl(var(--foreground-muted))] truncate" data-testid={`weather-glance-${firstDest}`}>
      {firstDest} · {tempStr}{condition ? ` · ${condition.toLowerCase()}` : ""}
    </span>
  );
}

// ───────────────────────────────────────────────────────────────────
// #departures — Trips leaving in next 30 days
// ───────────────────────────────────────────────────────────────────
function DeparturesSection({ data }) {
  const rows = data?.departures || [];
  return (
    <section id="departures" className={sectionClass} data-testid="overview-section-departures">
      <Heading level="eyebrow">Upcoming departures</Heading>
      <p className="mt-2 font-accent italic text-[13px] text-[hsl(var(--foreground-muted))]">
        {rows.length === 0 ? "No departures within the next month — a quiet horizon." : `${rows.length} departing within 30 days`}
      </p>
      {rows.length > 0 && (
        <ul className="mt-5 divide-y divide-[rgba(0,0,0,0.05)]" data-testid="overview-departure-list">
          {rows.map((j) => (
            <li key={j.id} data-testid={`overview-departure-${j.id}`}>
              <Link to={`/portal/advisor/journeys/${j.id}`} className="py-3 flex items-center gap-3 hover:bg-[hsl(var(--background-paper))]/60 rounded-md px-2 -mx-2 transition-colors">
                <Plane className="size-4 text-[hsl(var(--accent-gold))]" strokeWidth={1.5} />
                <div className="flex-1 min-w-0">
                  <p className="font-body text-[14px] truncate">{j.internal_trip_name || j.client_trip_title || "Experience"}</p>
                  <p className="font-accent italic text-[12.5px] text-[hsl(var(--foreground-muted))] truncate">{j.client_name}{j.destinations ? ` · ${j.destinations}` : ""}</p>
                  {j.destinations && <div className="mt-0.5"><WeatherGlance destination={j.destinations} /></div>}
                </div>
                <div className="flex items-center gap-2">
                  {j.any_masked && <Lock className="size-3 text-[hsl(var(--accent-gold))]" strokeWidth={1.7} />}
                  {j.blocker_count > 0 && (
                    <StatusTag tone="gold">{j.blocker_count} open</StatusTag>
                  )}
                  <StatusTag tone={j.days_to_departure <= 7 ? "gold" : "default"}>
                    {j.days_to_departure === 0 ? "Today" : `${j.days_to_departure}d · ${fmt.formatDate(j.trip_start_date)}`}
                  </StatusTag>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ───────────────────────────────────────────────────────────────────
// #alerts — Subtle operational alerts
// ───────────────────────────────────────────────────────────────────
function AlertsSection({ data }) {
  const rows = (data?.alerts || []).slice(0, 8);
  return (
    <section id="alerts" className={sectionClass} data-testid="overview-section-alerts">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <Heading level="eyebrow">Quiet operational alerts</Heading>
          <p className="mt-2 font-accent italic text-[13px] text-[hsl(var(--foreground-muted))]">
            {rows.length === 0 ? "Nothing flagged — every detail is in place." : "Small things worth attending to."}
          </p>
        </div>
      </div>
      {rows.length > 0 && (
        <ul className="mt-5 space-y-1.5" data-testid="overview-alert-list">
          {rows.map((a) => (
            <li key={a.id} data-testid={`overview-alert-${a.id}`}>
              <Link to={a.deeplink} className="flex items-center gap-3 py-2 px-3 rounded-md hover:bg-[hsl(var(--background-paper))] transition-colors">
                <CircleAlert className="size-3.5 text-[hsl(var(--accent-gold))]" strokeWidth={1.5} />
                <span className="font-body text-[13.5px] flex-1 min-w-0 truncate">{a.title}</span>
                <span className="font-accent italic text-[12px] text-[hsl(var(--foreground-muted))] truncate hidden sm:block max-w-[40%]">{a.detail}</span>
                <ChevronRight className="size-3 text-[hsl(var(--foreground-muted))]" strokeWidth={1.5} />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ───────────────────────────────────────────────────────────────────
// #financial — Outstanding / paid / next due
// ───────────────────────────────────────────────────────────────────
function FinancialSection({ data }) {
  const f = data?.financial || {};
  const tiles = [
    {
      label: "Outstanding",
      value: fmt.money(f.outstanding || 0, f.currency),
      hint: f.next_due_in_days != null
        ? (f.next_due_in_days >= 0 ? `next due in ${f.next_due_in_days}d` : `${Math.abs(f.next_due_in_days)}d overdue`)
        : (f.outstanding ? "no scheduled date" : "all clear"),
      tone: f.overdue_count > 0 ? "terracotta" : "default",
      testid: "overview-financial-outstanding",
    },
    {
      label: "Paid",
      value: fmt.money(f.paid || 0, f.currency),
      hint: "recorded to date",
      testid: "overview-financial-paid",
    },
    {
      label: "Overdue",
      value: f.overdue_count || 0,
      hint: f.overdue_count ? "needs follow-up" : "none — beautiful",
      tone: f.overdue_count > 0 ? "terracotta" : "default",
      testid: "overview-financial-overdue",
    },
  ];
  return (
    <section id="financial" className={sectionClass} data-testid="overview-section-financial">
      <Heading level="eyebrow">Financial snapshot</Heading>
      <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
        {tiles.map((t) => (
          <Link key={t.label} to="/portal/advisor/payments" className={`text-left p-4 rounded-md border bg-[hsl(var(--background-paper))] hover:bg-white transition-colors ${
            t.tone === "terracotta"
              ? "border-[hsl(var(--accent-terracotta))]/40 hover:border-[hsl(var(--accent-terracotta))]/60"
              : "border-[rgba(0,0,0,0.06)] hover:border-[hsl(var(--accent-gold))]/30"
          }`} data-testid={t.testid}>
            <p className="font-mono text-[9.5px] uppercase tracking-[0.20em] text-[hsl(var(--foreground-muted))]">{t.label}</p>
            <p className={`mt-1 font-serif text-[22px] ${t.tone === "terracotta" ? "text-[hsl(var(--accent-terracotta))]" : "text-[hsl(var(--foreground))]"}`}>{t.value}</p>
            <p className="mt-0.5 font-accent italic text-[12px] text-[hsl(var(--foreground-muted))]">{t.hint}</p>
          </Link>
        ))}
      </div>
    </section>
  );
}

// ───────────────────────────────────────────────────────────────────
// #workspace — Quick actions
// ───────────────────────────────────────────────────────────────────
function WorkspaceSection({ onNewJourney }) {
  const actions = [
    { icon: Plus, label: "New experience", testid: "ws-new-journey", onClick: onNewJourney },
    { icon: UserPlus, label: "Add client", testid: "ws-add-client", to: "/portal/advisor/clients?new=true" },
    { icon: ScrollText, label: "Draft agreement", testid: "ws-draft-agreement", to: "/portal/advisor/agreements" },
    { icon: Plane, label: "Open planning", testid: "ws-open-planning", to: "/portal/advisor/journeys" },
    { icon: Send, label: "Open concierge", testid: "ws-open-concierge", to: "/portal/advisor/concierge" },
  ];
  return (
    <section id="workspace" className={sectionClass} data-testid="overview-section-workspace">
      <Heading level="eyebrow">Workspace</Heading>
      <div className="mt-5 grid grid-cols-2 sm:grid-cols-5 gap-2">
        {actions.map((a) => {
          const Icon = a.icon;
          const inner = (
            <>
              <Icon className="size-4 text-[hsl(var(--accent-gold))]" strokeWidth={1.5} />
              <span className="font-body text-[13px] truncate">{a.label}</span>
            </>
          );
          const className = "flex items-center gap-2.5 p-3 rounded-md border border-[rgba(0,0,0,0.06)] bg-[hsl(var(--background-paper))] hover:bg-white hover:border-[hsl(var(--accent-gold))]/30 transition-colors";
          return a.onClick ? (
            <button key={a.label} type="button" onClick={a.onClick} className={className} data-testid={a.testid}>{inner}</button>
          ) : (
            <Link key={a.label} to={a.to} className={className} data-testid={a.testid}>{inner}</Link>
          );
        })}
      </div>
    </section>
  );
}

// ───────────────────────────────────────────────────────────────────
// #timeline — Recent activity feed
// ───────────────────────────────────────────────────────────────────
const TIMELINE_ICONS = {
  agreement_signed: ScrollText,
  payment_paid: CheckCircle2,
  document_uploaded: FileText,
  message_received: MessageSquare,
  message_sent: MessageSquare,
};

function TimelineSection({ data }) {
  const rows = (data?.timeline || []).slice(0, 14);
  return (
    <section id="timeline" className={sectionClass} data-testid="overview-section-timeline">
      <Heading level="eyebrow">Recent activity</Heading>
      {rows.length === 0 ? (
        <p className="mt-3 font-accent italic text-[13.5px] text-[hsl(var(--foreground-muted))]">A new day, ready to be written.</p>
      ) : (
        <ul className="mt-5 space-y-2.5" data-testid="overview-timeline-list">
          {rows.map((e) => {
            const Icon = TIMELINE_ICONS[e.kind] || Sparkles;
            return (
              <li key={e.id} data-testid={`overview-timeline-${e.id}`}>
                <Link to={e.deeplink} className="flex items-center gap-3 py-1.5 hover:bg-[hsl(var(--background-paper))]/60 rounded-md px-2 -mx-2 transition-colors">
                  <Icon className="size-3.5 text-[hsl(var(--accent-gold))] flex-shrink-0" strokeWidth={1.5} />
                  <div className="flex-1 min-w-0">
                    <p className="font-body text-[13.5px] truncate">
                      <span className="text-[hsl(var(--foreground-muted))]">{e.title}</span> · {e.detail}
                    </p>
                  </div>
                  <span className="font-mono text-[9.5px] uppercase tracking-[0.16em] text-[hsl(var(--foreground-muted))] tabular-nums">
                    {e.at ? fmt.formatDate(e.at) : ""}
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

// ───────────────────────────────────────────────────────────────────
// Main composite
// ───────────────────────────────────────────────────────────────────
export default function AdvisorOverviewComposite() {
  const { user } = useAuth() || {};
  const { data, loading, error } = useOverview();
  const [newJourneyOpen, setNewJourneyOpen] = React.useState(false);

  if (loading && !data) {
    return (
      <div className="space-y-6" data-testid="overview-loading">
        <Skeleton className="h-24" />
        <Skeleton className="h-40" />
        <Skeleton className="h-40" />
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="space-y-0 max-w-6xl"
      data-testid="advisor-overview-root"
    >
      {error && (
        <div className="mb-6 px-4 py-2.5 rounded bg-[#FCEAE0] border border-[#F0C8A8] text-[13px] text-[#8B4513]">
          {error}
        </div>
      )}
      <OverviewHeader user={user} data={data} onNewJourney={() => setNewJourneyOpen(true)} />
      <EcosystemHealthStrip />
      <UpcomingTriggersStrip />
      {/* Feb 2026 · Phase 1 — small "N replies need review" chip. Renders
          nothing when the inbox is calm. Deep-links to Concierge Desk. */}
      <div className="mt-4">
        <InboundRepliesChip />
      </div>
      <div className="mt-6 mb-6">
        <WhatChangedCard />
      </div>
      <div className="mt-10">
        <TodaySection data={data} />
        <JourneysSection data={data} />
        <ConciergeSection data={data} />
        <DeparturesSection data={data} />
        <AlertsSection data={data} />
        <FinancialSection data={data} />
        <WorkspaceSection onNewJourney={() => setNewJourneyOpen(true)} />
        <TimelineSection data={data} />
      </div>
      <NewJourneyModal open={newJourneyOpen} onClose={() => setNewJourneyOpen(false)} />
    </motion.div>
  );
}
